import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import { PageHeader, Loading, ErrorBox, Badge } from '../components/ui';

export function DispatchPage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['admin-dispatch'], queryFn: () => adminApi.dispatchList(), refetchInterval: 10_000 });
  const [selected, setSelected] = useState<string | null>(null);
  const items = (list.data as any)?.items ?? [];
  const activeId = selected ?? items[0]?.orderId;
  const detail = useQuery({ queryKey: ['admin-dispatch', activeId], queryFn: () => adminApi.dispatchDetail(activeId!), enabled: !!activeId, refetchInterval: 5_000 });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['admin-dispatch'] }); };
  const book = useMutation({ mutationFn: (partnerId?: string) => adminApi.dispatchBook(activeId!, partnerId), onSuccess: invalidate });
  const retry = useMutation({ mutationFn: () => adminApi.dispatchRetry(activeId!), onSuccess: invalidate });

  if (list.isLoading) return <Loading />;
  if (list.error) return <ErrorBox error={list.error} />;

  const d = detail.data as any;

  return (
    <div>
      <PageHeader title="Dispatch" subtitle="Rider selection across partner APIs" />
      <div className="grid grid-cols-[260px_1fr] gap-4">
        <div className="card p-3">
          <div className="font-semibold text-sm mb-2">Finding rider</div>
          <div className="space-y-1">
            {items.map((it: any) => (
              <button key={it.orderId} onClick={() => setSelected(it.orderId)} className="w-full text-left px-2 py-2 rounded-lg text-xs" style={it.orderId === activeId ? { background: 'var(--warn-bg-soft)' } : {}}>
                <div className="font-semibold">{it.orderId}</div>
                <div style={{ color: 'var(--text-faint)' }}>{it.store} · {it.area}</div>
                <div style={{ color: 'var(--danger)' }}>{it.waiting}</div>
              </button>
            ))}
            {!items.length && <div className="text-xs py-4 text-center" style={{ color: 'var(--text-faint)' }}>Nothing waiting for a rider</div>}
          </div>
        </div>

        {d && (
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-semibold">{d.order?.id}</div>
                <div className="text-xs" style={{ color: 'var(--text-faint)' }}>{d.order?.store} → {d.order?.customer} · {d.order?.items} · {d.order?.total?.formatted}</div>
              </div>
              <div className="flex gap-2">
                <button className="btn-outline" onClick={() => retry.mutate()} disabled={retry.isPending}>Retry dispatch</button>
                <button className="btn-accent" onClick={() => book.mutate(d.recommended)} disabled={book.isPending}>Book best rider</button>
              </div>
            </div>
            {d.emptyReason && <div className="text-xs mb-3" style={{ color: 'var(--warn-strong)' }}>{d.emptyReason}</div>}
            <table>
              <thead><tr><th>Partner</th><th>Vehicle</th><th>ETA</th><th>Distance</th><th>Price</th><th>Score</th><th /></tr></thead>
              <tbody>
                {(d.quotes ?? []).map((q: any) => (
                  <tr key={q.key}>
                    <td>{q.partner?.name ?? q.carrier}</td>
                    <td>{q.vehicleType}</td>
                    <td>{q.etaMinutes} min</td>
                    <td>{q.distanceKm} km</td>
                    <td>{q.price?.formatted}</td>
                    <td>{q.score}</td>
                    <td>{q.selected && <Badge tone="success">Best</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
